import { Flex, VStack, Image, IconButton } from '@chakra-ui/react'
import { useAnimation, Variants } from 'framer-motion'
import * as React from 'react'
import { useMediaQuery } from 'react-responsive'
import { ColorProps } from 'styled-system'
import { useAppContext } from '../../context/AppProvider'
import { NavItem } from '../../navigation'
import { images, theme } from '../../theme'
import Header from '../Header'
import SideBarItem from './SideBarItem'
import { MenuCont, MenuCont2, Overlay, RenderWrapper } from './styles'
import { useHistory } from 'react-router-dom'
import Previewer from './Previewer'
import { VscArrowLeft, VscArrowRight } from 'react-icons/vsc'
import { MENU1_WIDTH, MENU2_WIDTH, CONTACT_SUPPORT_FORM_ID } from '../../constants'
import { useGetMyUserQuery } from 'generated/graphql'
import { Sidetab } from '@typeform/embed-react'

type SideBarProps = ColorProps & {
  accentColor: string
  borderColor?: string
  closeOnNavigate?: boolean
  color: string
  hoverColor: string
  navItems: NavItem[]
  secondaryNavItems?: NavItem[]
  renderUserInfo?: (history: any) => React.ReactNode
  tooltipBg?: string
  tooltipColor?: string
  children?: React.ReactNode
}

const SideBar: React.FC<SideBarProps> = ({
  accentColor,
  bg,
  borderColor,
  children,
  color,
  hoverColor,
  navItems,
  secondaryNavItems,
  tooltipBg,
  tooltipColor
}) => {
  const {
    drawerOpen,
    toggleDrawer,
    toggleSecondaryDrawer,
    secondaryDrawerOpen,
    setIsAdminMode,
    isAdminMode
  } = useAppContext()
  const { data: me } = useGetMyUserQuery()

  const controls = useAnimation()
  const controls2 = useAnimation()

  const history = useHistory()

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 40em)' })

  React.useEffect(() => {
    if (location?.pathname.includes('dashboard')) {
      setIsAdminMode(true)
    } else {
      setIsAdminMode(false)
    }
  }, [location?.pathname])
  React.useEffect(() => {
    if (drawerOpen) {
      controls.start('open')
    } else {
      controls.start('closed')
    }
  }, [isTabletOrMobile, drawerOpen, controls])

  React.useEffect(() => {
    if (secondaryDrawerOpen) {
      controls2.start('open')
    } else {
      controls2.start('closed')
    }
  }, [isTabletOrMobile, secondaryDrawerOpen, controls2])

  const variants: Variants = {
    open: {
      x: 0,
      width: 200,
      transition: { staggerChildren: 0.2, delayChildren: 0.1, stiffness: 80 }
    },
    closed: {
      x: isTabletOrMobile ? -MENU1_WIDTH : 0,
      width: isTabletOrMobile ? MENU1_WIDTH : 64,
      transition: {
        stiffness: 80,
        staggerDirection: -1,
        staggerChildren: 0.2,
        delay: isTabletOrMobile ? 0.25 : 0
      }
    }
  }

  const variants2: Variants = {
    open: {
      x: 0,
      width: MENU2_WIDTH,
      transition: { staggerChildren: 0.05, delayChildren: 0.05, stiffness: 10, damping: 5 }
    },
    closed: {
      x: isTabletOrMobile ? MENU2_WIDTH : 0,
      width: isTabletOrMobile ? MENU2_WIDTH : 64,
      transition: {
        stiffness: 80,
        staggerDirection: -1,
        staggerChildren: 0.1
      }
    }
  }

  return isAdminMode ? (
    <>
      <Sidetab
        id={CONTACT_SUPPORT_FORM_ID}
        buttonColor={theme.colors.blue[100]}
        buttonText="Contact Us"
      />
      <MenuCont
        bg={bg as any}
        flexDir="column"
        animate={controls}
        variants={variants}
        alignItems="flex-start"
        justifyContent="flex-start"
        initial={{ width: drawerOpen ? MENU1_WIDTH : 64 }}
      >
        <Flex
          pl="20px"
          width="100%"
          height="64px"
          alignItems="center"
          borderBottomWidth={1}
          justifyContent="flex-start"
          borderColor={borderColor}
          p={0}
          bg="white"
          cursor="pointer"
          onClick={() => {
            if (isTabletOrMobile) {
              toggleDrawer()
            } else {
              history.push('/auth/dashboard/agent/dashboard')
            }
          }}
          border="none"
        >
          <Flex m={0} p={0} flex={1} justifyContent="center">
            <Image
              width={drawerOpen ? '65%' : '50%'}
              rounded="lg"
              src={
                drawerOpen
                  ? images.logo_purple
                  : me?.getMyUser?.agent?.agency?.logo?.url || images.icon
              }
              alt="Baotree Icon"
            />
          </Flex>
        </Flex>
        <Flex
          direction="column"
          overflow={drawerOpen ? 'hidden' : undefined}
          overflowY={drawerOpen ? 'auto' : undefined}
          width="100%"
          flex={1}
          justify="center"
        >
          {navItems.map((props) => (
            <SideBarItem
              color={color}
              key={props.title}
              hoverColor={hoverColor}
              accentColor={accentColor}
              tooltipColor={tooltipColor}
              tooltipBg={tooltipBg}
              {...props}
            />
          ))}
        </Flex>
        <VStack w="100%" mb={16}>
          {secondaryNavItems &&
            secondaryNavItems.map((props) => (
              <SideBarItem
                color={color}
                key={props.title}
                hoverColor={hoverColor}
                accentColor={accentColor}
                tooltipColor={tooltipColor}
                tooltipBg={tooltipBg}
                {...props}
              />
            ))}
        </VStack>
      </MenuCont>
      <MenuCont2
        bg={bg as any}
        flexDir="column"
        borderLeft="1px solid"
        borderLeftColor="gray.100"
        animate={controls2}
        variants={variants2}
        align="center"
        justifyContent="flex-start"
        initial={{ width: secondaryDrawerOpen ? MENU2_WIDTH : 64 }}
      >
        <Previewer />
      </MenuCont2>
      <IconButton
        onClick={() => toggleSecondaryDrawer()}
        my={6}
        position="fixed"
        bottom={6}
        size="lg"
        rounded="full"
        right={2}
        zIndex={4478}
        colorScheme="orange"
        aria-label="Open Secondary Drawer"
        icon={secondaryDrawerOpen ? <VscArrowRight /> : <VscArrowLeft />}
      />

      <RenderWrapper
        animate
        bg={`linear-gradient(to top, ${theme.colors.gray[100]} 50%, ${theme.colors.gray[200]} 50%)`}
        className="render-wrapper"
        pl={isTabletOrMobile ? 0 : drawerOpen ? '200px' : '64px'}
        pr={isTabletOrMobile ? 0 : secondaryDrawerOpen ? MENU2_WIDTH : '64px'}
      >
        <Header />
        {children}
        {isTabletOrMobile && (
          <Overlay
            onClick={toggleDrawer}
            initial={{ opacity: 0 }}
            pointerEvents={drawerOpen ? 'auto' : 'none'}
            animate={drawerOpen ? { opacity: 1 } : { opacity: 0 }}
          />
        )}
      </RenderWrapper>
    </>
  ) : (
    <>{children}</>
  )
}

export default SideBar

SideBar.defaultProps = {
  color: 'white',
  bg: 'gray.900',
  hoverColor: 'gray.800',
  borderColor: 'gray.700',
  accentColor: 'cyan.500'
}
